import { getIdToken } from 'firebase/auth';
import { useUserStore } from './auth';
import { useApiEnvStore } from './env';
import { WebOrder } from '@/types/webOrder';
import { SalesOrder } from '@/types/salesOrder';

async function getContext() {
	const envStore = useApiEnvStore();
	const userStore = useUserStore();

	if (!envStore.apiBaseUrl) {
		throw new Error('API base URL is not defined');
	}

	if (!userStore.user) {
		throw new Error('User is not authenticated');
	}

	const idToken = await getIdToken(userStore.user!, false);
	return { apiBaseUrl: envStore.apiBaseUrl, idToken, userId: userStore.user.uid };
}

//----------------------------------------------------------------------
// Types
//----------------------------------------------------------------------

export type ConsolidatedSKU = {
	sku: string;
	parentCode?: string;
	acumaticaQty?: number;
	acumaticaPrice?: number;
	acumaticaLocation?: string;
	shopifyQty?: number;
	shopifyPrice?: number;
	shopifyParentId?: string;
	shopifyTrackedStatus?: boolean;
	shopifyInventoryItemId?: string;
	shopifyVariants?: VariantIdSet[];
	shopifyOnly?: boolean;
	acumaticaOnly?: boolean;
	priceUpdateNeeded?: boolean;
	qtyUpdateNeeded?: boolean;
};

export type VariantIdSet = {
	variantId: string;
	inventoryItemId: string;
};

export type UpdatesNeededByParentCode = {
	parentCode: string;
	consolSKUs: ConsolidatedSKU[];
};

export type FetchConsolidatedSKUsResponse = {
	consolidateSKUs: ConsolidatedSKU[];
	updatesNeededByParentCode: UpdatesNeededByParentCode[];
};

export type FetchWebOrdersResponse = {
	webOrders: WebOrder[];
};

export type CreateSalesOrderResponse = {
	salesOrderCreated: boolean;
	userVerified: boolean;
};

export type LoginToAcumaticaResponse = {
	loginSuccess: boolean;
};

//----------------------------------------------------------------------
// GET
//----------------------------------------------------------------------

export async function fetchWebOrdersRaw() {
	const { apiBaseUrl, idToken } = await getContext();
	const response = await fetch(`${apiBaseUrl}/ListAllOpenShopifyOrders`, {
		method: 'GET',
		headers: { Authorization: `Bearer ${idToken}` }
	});
	if (!response.ok) {
		throw new Error(`Status: ${response.status}`);
	}
	const jsonResponse: WebOrder[] = await response.json();
	return jsonResponse;
}

export async function fetchConsolidatedSKUsRaw() {
	const { apiBaseUrl, idToken } = await getContext();
	const response = await fetch(`${apiBaseUrl}/ConsolidateProductSKUs`, {
		method: 'GET',
		headers: { Authorization: `Bearer ${idToken}` }
	});
	if (!response.ok) {
		throw new Error(`Status: ${response.status}`);
	}
	const jsonResponse: FetchConsolidatedSKUsResponse = await response.json();
	return jsonResponse;
}

//----------------------------------------------------------------------
// POST
//----------------------------------------------------------------------

export async function updateShopifySKUs(updateType: string, consolSKU: ConsolidatedSKU) {
	const { apiBaseUrl, idToken } = await getContext();
	const response = await fetch(`${apiBaseUrl}/UpdateShopifySKU`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${idToken}`
		},
		body: JSON.stringify({
			updateType,
			consolSKU
		})
	});
	if (!response.ok) {
		throw new Error(`Status: ${response.status}`);
	}
	return;
}

export async function createSalesOrder(salesOrder: SalesOrder): Promise<CreateSalesOrderResponse> {
	const { apiBaseUrl, idToken, userId } = await getContext();

	console.log(apiBaseUrl, idToken, userId);

	const response = await fetch(`${apiBaseUrl}/CreateSalesOrder`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${idToken}`
		},
		body: JSON.stringify({
			uid: userId,
			salesOrder
		})
	});
	if (!response.ok) {
		throw new Error(`Status: ${response.status}`);
	}

	const { salesOrderCreated, userVerified } = await response.json();
	return { salesOrderCreated, userVerified };
}

export async function loginToAcumatica(username: string, password: string): Promise<LoginToAcumaticaResponse> {
	const { apiBaseUrl, idToken, userId } = await getContext();

	const response = await fetch(`${apiBaseUrl}/LoginToAcumatica`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${idToken}`
		},
		body: JSON.stringify({
			uid: userId,
			username,
			password
		})
	});

	if (!response.ok) {
		throw new Error(`Status: ${response.status}`);
	}

	const { loginSuccess } = await response.json();
	return loginSuccess;
}
